import "../css/app.css";
import "./bootstrap";

// Na een deployment verwijzen gecachte pagina's naar chunks die niet meer bestaan (404).
// Vite's preloadError event wordt getriggerd bij gefaalde dynamic imports - herlaad de pagina.
window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    window.location.reload();
});

import * as Sentry from "@sentry/react";
import { createInertiaApp } from "@inertiajs/react";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";

const appName = import.meta.env.VITE_APP_NAME || "Laravel";
// Sentry DSN wordt gezet via VITE_SENTRY_DSN in .env
// Voor frontend moet je VITE_ prefix gebruiken zodat Vite de variabele beschikbaar maakt
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "production";

// Initialiseer Sentry als DSN beschikbaar is
if (sentryDsn) {
    Sentry.init({
        dsn: sentryDsn,
        environment: environment,
        // Setting this option to true will send default PII data to Sentry
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],
        tracesSampleRate: environment === "production" ? 0.1 : environment === "staging" ? 0.5 : 1.0,
        // Session Replay
        // For staging: 50% sample rate, for development: 100%, for production: 10%
        replaysSessionSampleRate: environment === "production" ? 0.1 : environment === "staging" ? 0.5 : 1.0,
        // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event, hint) {
            const error = hint?.originalException as any;
            if (error?._handled && error?.isAxiosError) {
                return null;
            }

            const stack = (hint?.originalException as Error)?.stack ?? "";
            const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
            const filename = frames[0]?.filename ?? "";
            const extensionPattern =
                /webkit-masked-url:|chrome-extension:|moz-extension:|safari-extension:|safari-web-extension:/;
            if (extensionPattern.test(stack) || extensionPattern.test(filename)) {
                return null;
            }

            if (event.request) {
                if (event.request.headers) {
                    delete event.request.headers["Authorization"];
                    delete event.request.headers["Cookie"];
                }
                if (event.request.data) {
                    const data = event.request.data as Record<string, any>;
                    if (data && typeof data === 'object' && 'password' in data) {
                        data.password = "[Filtered]";
                    }
                }
            }
            return event;
        },
    });
}

// Stale window.Ziggy na deploy: eenmalige reload zodat @routes opnieuw wordt ingeladen.
window.addEventListener("error", (event) => {
    const msg = event.error?.message ?? event.message ?? "";
    if (msg.includes("Ziggy error:") && msg.includes("is not in the route list")) {
        if (!sessionStorage.getItem("ziggy-reload")) {
            sessionStorage.setItem("ziggy-reload", "1");
            window.location.reload();
        }
    }
});
window.addEventListener("load", () => {
    sessionStorage.removeItem("ziggy-reload");
});

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.tsx`,
            import.meta.glob("./Pages/**/*.tsx"),
        ).catch((error) => {
            const msg = error?.message ?? "";
            const isChunkLoadError =
                msg.includes("Failed to fetch dynamically imported module") ||
                msg.includes("Importing a module script failed") ||
                msg.includes("Loading chunk") ||
                msg.includes("Failed to load module script");
            if (isChunkLoadError) {
                window.location.reload();
                return new Promise(() => {}); // Houd promise pending (pagina herlaadt)
            }
            throw error;
        }),
    setup({ el, App, props }) {
        const root = createRoot(el);

        // Voeg user context toe aan Sentry
        if (sentryDsn && props.auth?.user) {
            Sentry.setUser({
                id: props.auth.user.id.toString(),
                email: props.auth.user.email,
                username: props.auth.user.name,
            });
        }

        // Track Inertia page visits als breadcrumbs
        if (sentryDsn && props.page?.component) {
            Sentry.addBreadcrumb({
                category: "navigation",
                message: `Page: ${props.page.component}`,
                level: "info",
                data: {
                    url: window.location.href,
                    component: props.page.component,
                },
            });
        }

        root.render(
            <>
                <App {...props} />
                <Toaster 
                    richColors 
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: 'rgb(15 23 42 / 0.95)',
                            border: '1px solid rgb(30 41 59)',
                            color: 'white',
                        },
                    }}
                />
            </>
        );
    },
    progress: {
        color: "#4B5563",
    },
});
